import sanitize from "sanitize-html";
import { z } from "zod";

/**
 * Strips HTML tags then decodes entities that sanitize-html adds for HTML-output context.
 * Safe because tags are already removed and the plain text result is consumed by
 * React JSX / JSON APIs, which handle their own escaping on render. Decoding &lt;/&gt; lets
 * literal "<"/">" typed by the user round-trip as text instead of showing as "&lt;"/"&gt;".
 */
function sanitizePlainText(val: string): string {
  return sanitize(val, { allowedTags: [], allowedAttributes: {} })
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&"); // decode &amp; LAST so a literal "&amp;lt;" stays "&lt;", never becomes "<"
}

/** Zod type: sanitizes HTML tags. Use for titles, names, etc. */
export const zPlainText = z.string().overwrite(sanitizePlainText);

/** Zod type: sanitizes HTML tags and rejects empty results. Use for mandatory plain-text fields. */
export const zPlainTextRequired = z
  .string()
  .overwrite(sanitizePlainText)
  .pipe(z.string().min(1, "This field is required"));

/** Zod type: rejects if value contains dangerous HTML. Allows safe rich-text tags. Use for descriptions, notes, comment bodies. */
export const zSafeHtml = z.string().overwrite((val) =>
  sanitize(val, {
    allowedTags: sanitize.defaults.allowedTags.concat(["img", "span", "u", "s", "sub", "sup", "mark"]),
    allowedAttributes: {
      ...sanitize.defaults.allowedAttributes,
      img: ["src", "alt", "width", "height"],
      span: ["style", "class"],
      "*": ["class"],
    },
    allowedSchemes: ["http", "https", "mailto"],
  }),
);
